import { useCallback, useMemo } from 'react';

import { fetchUserList } from '#/api/system/user';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import AccessStatusToggle from '#/modules/infra/settings/access/components/AccessStatusToggle';
import { buildUserColumns } from '#/modules/infra/settings/access/data/userColumns';
import useAccessList, { ACCESS_LIST_PAGE_SIZE } from '#/modules/infra/settings/access/hooks/useAccessList';

const USER_LIST_QUERY_KEY = ['system', 'user', 'list'];
const USER_DEFAULT_SORT = [{ id: 'account', desc: false }];

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

const UserListPage = () => {
  const canCreate = useCan(PERM.SYS_ACCOUNT_CREATE);
  const canUpdate = useCan(PERM.SYS_ACCOUNT_UPDATE);
  const canReadAccount = useCan(PERM.SYS_ACCOUNT_READ);
  const canReadRole = useCan(PERM.SYS_ROLE_READ);
  const canOpenAccess = canReadAccount && canReadRole;

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
  } = useAccessList({
    queryKeyBase: USER_LIST_QUERY_KEY,
    fetchList: fetchUserList,
    defaultSort: USER_DEFAULT_SORT,
  });

  const columns = useMemo(
    () => buildUserColumns({ canUpdate, canOpenAccess }),
    [canUpdate, canOpenAccess],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({ id: item.id, desc: Boolean(item.desc) })));
  }, [setSort]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.systemUserCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">帳號管理</span>
        </h1>
        <div className="table-panel min-h-0 flex-1">
          <ListTablePanel
            tableKey="sys.user.list"
            columns={columns}
            rows={rows}
            pagination={pagination}
            initialPageSize={ACCESS_LIST_PAGE_SIZE}
            isLoading={isLoading}
            isError={isError}
            errorMessage={isError ? getErrorMessage(error, '載入帳號列表失敗') : undefined}
            onRetry={isError ? () => { refetch(); } : undefined}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onSearch={setKeyword}
            onReset={resetListQuery}
            initialKeyword={listQuery.keyword}
            searchPlaceholder="搜尋帳號、姓名或編號"
            toolbarAfterSearch={(
              <AccessStatusToggle
                value={[].concat(listQuery.filters?.status ?? '')[0] ?? ''}
                onChange={(value) => setFilter('status', value)}
              />
            )}
            headerAction={headerAction}
            listQuery={listQuery}
            onSortRemove={removeSortAt}
            onSortingChange={handleSortingChange}
            tableClassName="table_compact"
            textSize="sm"
          />
        </div>
      </div>
    </PageShell>
  );
};

export default UserListPage;
